/**
 * What the accounts screens read: people's accounts, their devices and
 * their sign-in history from hg-auth, and a client's patients, addresses and
 * open orders from hg-order. Every key starts with `ACCOUNTS_KEY`, so an
 * action on an account refreshes everything read about accounts in one call.
 *
 * hg-auth's staff endpoints are an admin's and a superadmin's; a dispatcher
 * is refused (`403`). The screens ask only for what the person's
 * `permissions` open (see `routes/`).
 */
import { infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/react-query";

import { authApi, contentApi, notifyApi, orderApi, unwrap } from "@/shared/api/clients";
import { instantIn, MARKET_TIME_ZONE } from "@/shared/lib/format";

import type { AccountStatus, Sort, UserRole } from "./types";

export const ACCOUNTS_KEY = ["accounts"] as const;

/** Rows of a list page. */
export const PER_PAGE = 25;
/** Lines of a history read at a time. */
export const HISTORY_LIMIT = 20;

/** The filters `GET /v2/admin/users` has. There are no others. */
export interface AccountsFilter {
  role?: UserRole;
  status?: AccountStatus;
  q?: string;
  sort?: Sort;
}

export function accountsQuery(filter: AccountsFilter, page: number, perPage = PER_PAGE) {
  return queryOptions({
    queryKey: [...ACCOUNTS_KEY, "list", filter, page, perPage] as const,
    queryFn: ({ signal }) =>
      unwrap(
        authApi.GET("/v2/admin/users", {
          params: { query: { ...filter, page, per_page: perPage } },
          signal,
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

export function accountKey(userId: string) {
  return [...ACCOUNTS_KEY, "one", userId] as const;
}

export function accountQuery(userId: string) {
  return queryOptions({
    queryKey: accountKey(userId),
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        authApi.GET("/v2/admin/users/{user_id}", {
          params: { path: { user_id: userId } },
          signal,
        }),
      );
      return data;
    },
  });
}

/** The devices a person is signed in on, the one used last first. */
export function sessionsQuery(userId: string) {
  return queryOptions({
    queryKey: [...accountKey(userId), "sessions"] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        authApi.GET("/v2/admin/users/{user_id}/sessions", {
          params: { path: { user_id: userId }, query: { limit: 50 } },
          signal,
        }),
      );
      return data;
    },
  });
}

/** A person's sign-in history, newest first, read a page at a time by cursor. */
export function authEventsQuery(userId: string) {
  return infiniteQueryOptions({
    queryKey: [...accountKey(userId), "events"] as const,
    queryFn: ({ pageParam, signal }) =>
      unwrap(
        authApi.GET("/v2/admin/users/{user_id}/auth-events", {
          params: {
            path: { user_id: userId },
            query: { limit: HISTORY_LIMIT, ...(pageParam === null ? {} : { cursor: pageParam }) },
          },
          signal,
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) =>
      last.pagination.has_more ? (last.pagination.next_cursor ?? null) : null,
  });
}

/** How far the erasure of a deleted account has come; `404` when none was asked for. */
export function erasureQuery(userId: string) {
  return queryOptions({
    queryKey: [...accountKey(userId), "erasure"] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        authApi.GET("/v2/admin/users/{user_id}/erasure", {
          params: { path: { user_id: userId } },
          signal,
        }),
      );
      return data;
    },
    retry: false,
  });
}

/** The service areas an admin or a dispatcher works in. */
export function staffAreasQuery(userId: string) {
  return queryOptions({
    queryKey: [...accountKey(userId), "areas"] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        authApi.GET("/v2/admin/users/{user_id}/areas", {
          params: { path: { user_id: userId } },
          signal,
        }),
      );
      return data.area_ids;
    },
  });
}

/** Blocked numbers, newest first: the open blocks, or the lifted ones too. */
export function phoneBlocksQuery(all: boolean, page: number, perPage = PER_PAGE) {
  return queryOptions({
    queryKey: [...ACCOUNTS_KEY, "phone-blocks", all, page, perPage] as const,
    queryFn: ({ signal }) =>
      unwrap(
        authApi.GET("/v2/admin/phone-blocks", {
          params: { query: { page, per_page: perPage, ...(all ? { all: true } : {}) } },
          signal,
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

/**
 * What hg-order knows of a client: who they order for, where to, and what
 * they have under way in the caller's areas. `404` for an account Order does
 * not know as a client's.
 */
export function clientCardQuery(userId: string) {
  return queryOptions({
    queryKey: [...ACCOUNTS_KEY, "card", userId] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        orderApi.GET("/v2/admin/clients/{user_id}", {
          params: { path: { user_id: userId } },
          signal,
        }),
      );
      return data;
    },
    retry: false,
  });
}

/** What a client's orders add up to, within the caller's areas: how many, how they stand, what they came to. */
export function clientSummaryQuery(userId: string) {
  return queryOptions({
    queryKey: [...accountKey(userId), "order-summary"] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        orderApi.GET("/v2/admin/clients/{user_id}/summary", {
          params: { path: { user_id: userId } },
          signal,
        }),
      );
      return data;
    },
  });
}

/** How a client's list of orders is narrowed: by how they stand, and by the days they were made. */
export interface ClientOrdersFilter {
  status?: "open" | "completed" | "cancelled";
  /** Days as `YYYY-MM-DD`, by the market's clock. */
  from?: string;
  to?: string;
}

/** The day after a day, both as `YYYY-MM-DD`. */
function dayAfter(day: string): string {
  const next = new Date(`${day}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString().slice(0, 10);
}

/**
 * A client's orders, the newest first, a page at a time: every one hg-order
 * has of them in the caller's areas. The days are the market's: from the
 * start of the first to the start of the day after the last.
 */
export function clientOrdersQuery(
  userId: string,
  filter: ClientOrdersFilter,
  page: number,
  perPage: number,
) {
  const from =
    filter.from === undefined ? null : instantIn(`${filter.from}T00:00`, MARKET_TIME_ZONE);
  const to =
    filter.to === undefined ? null : instantIn(`${dayAfter(filter.to)}T00:00`, MARKET_TIME_ZONE);
  return queryOptions({
    queryKey: [
      ...accountKey(userId),
      "orders",
      filter.status ?? null,
      from,
      to,
      page,
      perPage,
    ] as const,
    queryFn: ({ signal }) =>
      unwrap(
        orderApi.GET("/v2/admin/clients/{user_id}/orders", {
          params: {
            path: { user_id: userId },
            query: {
              page,
              per_page: perPage,
              ...(filter.status === undefined ? {} : { status: filter.status }),
              ...(from === null ? {} : { created_from: from }),
              ...(to === null ? {} : { created_to: to }),
            },
          },
          signal,
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

/** How many of the newest messages a client's page shows; the whole list is growth's screen. */
export const LAST_MESSAGES = 6;

/**
 * The last messages a person was sent, the newest first: hg-notify's list, its first few.
 *
 * Its key is not the account's: nothing done to an account changes what the
 * person was sent, and an action's dialog waits for the account's reads to
 * come back before it closes. hg-notify being slow or away must not hold it.
 */
export function lastMessagesQuery(userId: string) {
  return queryOptions({
    queryKey: ["account-messages", userId] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        notifyApi.GET("/v2/admin/users/{user_id}/notifications", {
          params: { path: { user_id: userId }, query: { limit: LAST_MESSAGES } },
          signal,
        }),
      );
      return data;
    },
  });
}

/**
 * What a person accepted, the newest first, from hg-content: which wording,
 * in which app, in which language, for whom. A superadmin's alone: a
 * personal record, not content (hg-content answers anybody else `403`).
 */
export function consentsQuery(userId: string) {
  return infiniteQueryOptions({
    queryKey: [...accountKey(userId), "consents"] as const,
    queryFn: ({ pageParam, signal }) =>
      unwrap(
        contentApi.GET("/v2/admin/users/{user_id}/consents", {
          params: {
            path: { user_id: userId },
            query: { limit: HISTORY_LIMIT, ...(pageParam === null ? {} : { cursor: pageParam }) },
          },
          signal,
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) =>
      last.pagination.has_more ? (last.pagination.next_cursor ?? null) : null,
  });
}

// ---- one's own account ----

/** What one's own account reads under: the profile's key, so a change to it refreshes both. */
export const MY_SESSIONS_KEY = ["auth", "me", "sessions"] as const;

/** The devices the person looking is signed in on, this one marked: every app, not only this one. */
export const mySessionsQuery = queryOptions({
  queryKey: MY_SESSIONS_KEY,
  queryFn: async ({ signal }) => {
    const { data } = await unwrap(
      authApi.GET("/v2/sessions", { params: { query: { limit: 50 } }, signal }),
    );
    return data;
  },
});

/** Rows of a person's SMS read at a time. */
export const SMS_LIMIT = 10;

/**
 * Every SMS that went to a person's number, the newest first, a slice at a
 * time: hg-notify's, an admin's and a superadmin's. A one-time code comes
 * with its digits already hidden by hg-notify; each read is kept in its log.
 */
export function personSmsQuery(userId: string) {
  return infiniteQueryOptions({
    queryKey: [...accountKey(userId), "sms"] as const,
    queryFn: ({ pageParam, signal }) =>
      unwrap(
        notifyApi.GET("/v2/admin/users/{user_id}/sms-messages", {
          params: {
            path: { user_id: userId },
            query: { limit: SMS_LIMIT, ...(pageParam === null ? {} : { cursor: pageParam }) },
          },
          signal,
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) =>
      last.pagination.has_more ? (last.pagination.next_cursor ?? null) : null,
  });
}
